import { useEffect, useState } from 'react'

import {
  HANDSET_MAX_WIDTH,
  NARROW_MAX_WIDTH,
  coarsePointerBelowDesktop,
} from 'src/styles/breakpoints'

/**
 * The JS half of `src/styles/breakpoints` (PRO-929), for the few places a
 * media query cannot reach: choosing a Drawer over a Sider, choosing a click
 * trigger over a hover one, sending Intercom new boot settings.
 *
 * `matchMedia` rather than a `resize` listener on purpose. A resize listener
 * fires continuously through a drag and makes every subscriber re-render on
 * every frame; a media-query listener fires only when the answer actually
 * changes, which for these consumers is once per crossing. The shell already
 * pays for one resize listener and does not need N more.
 *
 * SSR / jsdom: `matchMedia` is absent in jsdom unless a test installs it, so
 * both hooks fall back to the DESKTOP answer. That direction is deliberate
 * and matches `TableHeader`'s "an unmeasured 0-width must not collapse"
 * guard — an unmeasured environment renders the full desktop UI rather than
 * a stripped-down one, so a missing API can never silently hide a control.
 */
/**
 * ONE `MediaQueryList` AND ONE NATIVE LISTENER PER QUERY, shared by every
 * subscriber — the shape `src/design-mode/store.ts` already uses.
 *
 * `ActionButtons` reads two of these hooks and renders once per table ROW, so
 * the per-instance form built a `MediaQueryList` in the state initialiser AND
 * another in the effect, for each hook, for each row: ~400 objects and 200
 * retained `change` listeners on a 100-row list. Now a row costs a `Set`
 * entry and a store read. (A band crossing still re-renders every subscriber,
 * because each holds its own state — that is inherent, not the part this
 * removes.)
 *
 * KEYED ON THE `matchMedia` FUNCTION AS WELL AS THE QUERY. Suites install
 * their own `matchMedia` per case, and a module-level cache that ignored that
 * would hand the second case the first case's answers. Swapping the function
 * drops the cache, which is also the honest reading: a different
 * `matchMedia` is a different environment.
 */
type QueryStore = { mql: MediaQueryList; subscribers: Set<() => void> }

const queryStores = new Map<string, QueryStore>()
let storeFactory: typeof window.matchMedia | undefined

const getQueryStore = (query: string): QueryStore | null => {
  if (typeof window === 'undefined' || !window.matchMedia) return null

  if (storeFactory !== window.matchMedia) {
    queryStores.clear()
    storeFactory = window.matchMedia
  }

  const cached = queryStores.get(query)
  if (cached) return cached

  const mql = window.matchMedia(query)
  const store: QueryStore = { mql, subscribers: new Set() }
  mql.addEventListener('change', () => {
    store.subscribers.forEach((notify) => notify())
  })
  queryStores.set(query, store)
  return store
}

const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(
    () => getQueryStore(query)?.mql.matches ?? false
  )

  useEffect(() => {
    const store = getQueryStore(query)
    if (!store) return undefined
    const read = () => setMatches(store.mql.matches)
    // Keep state honest if the query, or the environment, changed between
    // render and effect.
    read()
    store.subscribers.add(read)
    return () => {
      store.subscribers.delete(read)
    }
  }, [query])

  return matches
}

const BELOW_DESKTOP_QUERY = `(max-width: ${NARROW_MAX_WIDTH}px)`
const HANDSET_QUERY = `(max-width: ${HANDSET_MAX_WIDTH}px)`

/** True below antd `lg` (992px) — the band this responsive work may change. */
export const useIsBelowDesktop = (): boolean =>
  useMediaQuery(BELOW_DESKTOP_QUERY)

/** True below antd `md` (768px) — phones, where the rail becomes a drawer. */
export const useIsHandset = (): boolean => useMediaQuery(HANDSET_QUERY)

/**
 * The same answer as `useIsBelowDesktop`, for code that runs from an event
 * handler rather than from a render — the shape `getDesignMode()` uses for the
 * imperative `Modal.confirm` statics. Same query and same desktop fallback, so
 * the two can never disagree about where the floor is.
 */
export const isBelowDesktop = (): boolean =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia(BELOW_DESKTOP_QUERY).matches

/**
 * True where the primary pointer cannot hover AND the viewport is inside the
 * narrow band — the right question for "should this menu open on hover".
 *
 * Both halves, never one: the pointer answers the question correctly but is
 * unbounded by width, and >= 992px must behave exactly as it does today. See
 * `coarsePointerBelowDesktop`, which this shares its query with.
 */
export const useIsCoarsePointerBelowDesktop = (): boolean =>
  useMediaQuery(coarsePointerBelowDesktop.replace('@media ', ''))
